import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLiveRefresh } from '../context/LiveUpdatesContext';
import { notificationService } from '../services';
import {
  TABS, categoryOf, sectionFor, iconFor, isHighPriority, timeLabel, dayGroup,
} from '../utils/notificationMeta';

// Roles the dashboard notification endpoints serve. The carer roles are
// included because a carer whose manager also granted them dashboard modules is
// a dashboard user too — the API gates them on their menus, not their role.
// The agency administrator too: the API has always served them
// (isManagerOrAgencyAdmin) and every agency notification is addressed to them —
// without the bell they were stored but never seen.
const DASHBOARD_NOTIF_ROLES = [
  'carehome_owner', 'domiciliary_owner',
  'carehome_manager', 'domiciliary_manager', 'staffs',
  'carehome_employee', 'domiciliary_employee',
  'agency_owner',
];

/**
 * Notification bell + dropdown panel (below the bell).
 * - Click bell → small panel with recent notifications, filter tabs, "Mark all
 *   read" and "View all notifications".
 * - Click an item → mark read + navigate to the relevant SECTION (not a detail
 *   popup).
 */
const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const roleCode = user?.role?.role_code;
  const enabled = roleCode && DASHBOARD_NOTIF_ROLES.includes(roleCode);

  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [tab, setTab] = useState('All');
  const wrapRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await notificationService.getUnreadCount();
      setUnreadCount(res?.data?.unreadCount || 0);
    } catch (_) { /* ignore */ }
  }, []);

  // Badge on load; after that the live refresh says when something arrived
  // (or was read in another tab). The slow poll is only a safety net.
  useEffect(() => {
    if (!enabled) return undefined;
    fetchCount();
    const id = setInterval(fetchCount, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [enabled, fetchCount]);

  const loadList = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await notificationService.getNotifications(1, 20);
      setItems(res?.data?.notifications || []);
      setUnreadCount(res?.data?.unreadCount ?? 0);
    } catch (_) {
      if (!quiet) setItems([]);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  // A new notification: the badge updates at once, and an open panel gets it
  // added in place.
  useLiveRefresh(['notifications'], () => {
    if (!enabled) return;
    if (open) loadList({ quiet: true });
    else fetchCount();
  });

  // Fetch the list each time the panel opens.
  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  // Close on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleItemClick = async (item) => {
    if (!item.is_read) {
      setItems((prev) => prev.map((n) => (n.id === item.id ? { ...n, is_read: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
      notificationService.markRead(item.id).catch(() => {});
    }
    setOpen(false);
    navigate(sectionFor(item));
  };

  const handleMarkAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    notificationService.markAllRead().catch(() => {});
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  if (!enabled) return null;

  const filtered = tab === 'All' ? items : items.filter((n) => categoryOf(n.type) === tab);

  // Group into TODAY / EARLIER, preserving created_at DESC order.
  const groups = [];
  filtered.forEach((n) => {
    const label = dayGroup(n.created_at);
    let g = groups.find((x) => x.label === label);
    if (!g) { g = { label, items: [] }; groups.push(g); }
    g.items.push(n);
  });

  return (
    <div className="relative" ref={wrapRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex items-center justify-center h-9 w-9 bg-white dark:bg-[#31373f] border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-[#3a4048] transition-colors"
        title="Notifications"
      >
        <Bell className="w-[18px] h-[18px] text-gray-600 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white dark:ring-[#31373f]" />
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] w-[380px] sm:w-[420px] bg-white dark:bg-[#262b33] border border-gray-100 dark:border-gray-800 rounded-2xl shadow-[0_16px_50px_rgba(0,0,0,0.18)] z-30 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-4 pb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-[16px] font-bold text-gray-800 dark:text-gray-100">Notifications</h3>
              {unreadCount > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            <button
              onClick={handleMarkAll}
              className="text-[13px] font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-300"
              disabled={unreadCount === 0}
            >
              Mark all read
            </button>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2 px-5 pb-3">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold border transition ${
                  tab === t
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-transparent text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300'
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="max-h-[420px] overflow-y-auto border-t border-gray-100 dark:border-gray-800">
            {loading && (
              <p className="text-[13px] text-gray-400 text-center py-10">Loading…</p>
            )}
            {!loading && filtered.length === 0 && (
              <div className="flex flex-col items-center py-12">
                <Bell className="w-8 h-8 text-gray-200 dark:text-gray-700" />
                <p className="text-[13px] text-gray-400 mt-2">You're all caught up</p>
              </div>
            )}

            {!loading && groups.map((group) => (
              <div key={group.label}>
                <p className="px-5 pt-3 pb-1.5 text-[11px] font-bold text-gray-400 dark:text-gray-500 tracking-wider">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  const { Icon, ring } = iconFor(item.type);
                  const high = isHighPriority(item);
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleItemClick(item)}
                      className={`w-full text-left flex gap-3 px-5 py-3 border-l-[3px] transition hover:bg-gray-50 dark:hover:bg-[#262626] ${
                        high
                          ? 'border-rose-500 bg-rose-50/60 dark:bg-rose-500/10'
                          : item.is_read
                          ? 'border-transparent'
                          : 'border-blue-500 bg-blue-50/40 dark:bg-blue-500/5'
                      }`}
                    >
                      <div className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${ring}`}>
                        <Icon className="w-[18px] h-[18px]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-[13.5px] leading-snug truncate ${item.is_read ? 'font-semibold text-gray-700 dark:text-gray-300' : 'font-bold text-gray-800 dark:text-gray-100'}`}>
                            {item.title}
                          </p>
                          <span className="text-[11px] text-gray-400 whitespace-nowrap shrink-0 mt-0.5">
                            {timeLabel(item.created_at)}
                          </span>
                        </div>
                        <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                          {item.body}
                        </p>
                      </div>
                      {!item.is_read && (
                        <span className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${high ? 'bg-rose-500' : 'bg-blue-500'}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Footer */}
          <button
            onClick={handleViewAll}
            className="w-full py-3.5 text-[13px] font-semibold text-blue-600 hover:text-blue-700 border-t border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-[#262626] transition"
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
